import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';
import React from 'react';
import classNames from 'classnames';
import { vars } from '$util/theme';
import type { IconProps } from '@/components/atoms/Icon';
import { Icon } from '@/components/atoms/Icon';
import { LoadingIndicator } from '../LoadingIndicator';
import type { ButtonSizes } from './constants';
import { ICON_SIZE_MAP, DesignType } from './constants';

import styles from './index.module.scss';

export * from './constants';

interface ButtonBaseProps {
  readonly size?: ButtonSizes;
  readonly designType?: DesignType;
  readonly fullWidth?: boolean;
  readonly type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  readonly disabled?: boolean;
  readonly iconId?: IconProps['id'];
  readonly loading?: boolean;
  readonly noIcon?: boolean;
  readonly noShadow?: boolean;
}

interface HTMLAnchorProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  readonly href: string;
}
interface HTMLButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly rel?: never;
}

export type ButtonProps = ButtonBaseProps & (HTMLButtonProps | HTMLAnchorProps);

function ButtonIconComponent({
  id,
  color = '#FFFFFF',
  size = 24,
}: {
  readonly id: IconProps['id'];
  readonly size?: IconProps['width'];
  readonly color?: IconProps['color'];
}) {
  return <Icon className={styles.Icon} id={id} legacy={false} color={color} width={size} height={size} />;
}

export function Button({
  size = 'M',
  type = 'button',
  fullWidth,
  designType = DesignType.PRIMARY,
  disabled,
  children,
  className,
  loading,
  iconId = 'arrows/arrow-right',
  noIcon,
  noShadow,
  ...props
}: ButtonProps): JSX.Element {
  const consolidatedClassNames = classNames(
    styles.Button,
    styles[size],
    fullWidth ? styles.fullWidth : '',
    styles[designType],
    (designType === DesignType.SECONDARY_DARK || designType === DesignType.SECONDARY_LIGHT) &&
      styles.secondary,
    loading && styles.loading,
    noShadow && styles.NoShadow,
    className
  );

  let iconColor;
  switch (designType) {
    case DesignType.CTA_LIGHT:
      iconColor = vars.quantumverse.reskinColors.purple700;
      break;
    case DesignType.CTA_DARK:
    case DesignType.PRIMARY:
      iconColor = '#FFFFFF';
      break;
    default:
      iconColor = 'currentColor';
  }

  const iconSize = ICON_SIZE_MAP.get(designType)?.get(size);

  if ('href' in props) {
    return (
      <a className={classNames(consolidatedClassNames, disabled && styles.disabled)} {...props}>
        {children && (
          <div className={styles.ButtonContainer}>
            {children}
            {!noIcon && (
              <div className={styles.ButtonIcon}>
                {loading ? (
                  <LoadingIndicator className={styles.ButtonLoading} />
                ) : (
                  <ButtonIconComponent id={iconId} size={iconSize} color={iconColor} />
                )}
              </div>
            )}
          </div>
        )}
      </a>
    );
  }

  return (
    <button
      // eslint-disable-next-line react/button-has-type -- incorrect rule
      type={type}
      className={consolidatedClassNames}
      disabled={disabled}
      {...props}
    >
      {children && (
        <div className={styles.ButtonContainer}>
          {children}
          {!noIcon && (
            <div className={styles.ButtonIcon}>
              {loading ? (
                <LoadingIndicator className={styles.ButtonLoading} />
              ) : (
                <ButtonIconComponent id={iconId} size={iconSize} color={iconColor} />
              )}
            </div>
          )}
        </div>
      )}
    </button>
  );
}
Button.displayName = 'Button';
